import React, { useState, useEffect } from 'react';

/** Deterministic hue from a ticker string. */
export function coinHue(t: string): number {
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) % 360;
  return h;
}

const baseTicker = (t: string) => t.split('_')[0].toUpperCase();

interface CoinAvatarProps {
  ticker: string;
  size?: number;
  className?: string;
  /** Render the avatar with a transparent hole in the middle (ring style). */
  ring?: boolean;
  /** Optional coin logo URL. Falls back to gradient initials on error or absence. */
  imageUrl?: string;
}

/**
 * Gradient coin avatar showing the first two letters of the ticker.
 * When an imageUrl is supplied, the logo is layered inside the circle;
 * any load error hides the image and the gradient initials remain.
 */
export const CoinAvatar: React.FC<CoinAvatarProps> = ({ ticker, size = 32, className = '', ring = false, imageUrl }) => {
  const h1 = coinHue(ticker);
  const h2 = (h1 + 40) % 360;
  const initials = baseTicker(ticker).slice(0, 2);

  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [imageUrl]);

  const showImg = imageUrl && !imgFailed;

  return (
    <div
      className={`coin-avatar ${className}`.trim()}
      style={{
        width: size,
        height: size,
        background: ring
          ? `conic-gradient(from 180deg, hsl(${h1},70%,58%), hsl(${h2},72%,46%), hsl(${h1},70%,58%))`
          : `linear-gradient(135deg, hsl(${h1}, 70%, 58%), hsl(${h2}, 72%, 46%))`,
        fontSize: size * 0.36,
      }}
      aria-hidden="true"
    >
      {initials}
      {showImg && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setImgFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            borderRadius: '50%',
          }}
        />
      )}
    </div>
  );
};
